import { useState, useEffect } from "react";
import { Button } from "@/Components/ui/button";
import {
   Dialog,
   DialogContent,
   DialogDescription,
   DialogHeader,
   DialogTitle,
   DialogTrigger,
} from "@/Components/ui/dialog";
import { ScrollArea } from "@/Components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/Components/ui/tabs";
import { Textarea } from "@/Components/ui/textarea";
import { ImageType } from "@/types/productTypes";
import { Plus, Edit, Trash2, ImageIcon } from "lucide-react";
import fetchDb from "@/utils/customRequestFunction";
import ImagePickerDialog from "@/Components/ImagePicker/ImagePickerDialog";
import { useToast } from "@/hooks/use-toast";

interface LearnMoreItem {
   id?: number;
   content: string;
   imageId?: number | null;
   image?: ImageType | null;
}

interface LearnMoreEditorProps {
   onContentCreated?: () => void;
   initialItemToEdit?: LearnMoreItem | null;
}

export default function LearnMoreEditor({
   onContentCreated,
   initialItemToEdit = null,
}: LearnMoreEditorProps = {}) {
   const { toast } = useToast();
   const [isOpen, setIsOpen] = useState(false);
   const [learnMoreItems, setLearnMoreItems] = useState<LearnMoreItem[]>([]);
   const [currentItem, setCurrentItem] = useState<LearnMoreItem | null>(null);
   const [isImagePickerOpen, setIsImagePickerOpen] = useState(false);
   const [isEditing, setIsEditing] = useState(false);
   const [isLoading, setIsLoading] = useState(false);
   const [activeTab, setActiveTab] = useState("list");

   // Fetch learn more items when dialog opens
   useEffect(() => {
      if (isOpen) {
         fetchLearnMoreItems();
      }
   }, [isOpen]);

   const fetchLearnMoreItems = async () => {
      try {
         setIsLoading(true);
         const items = await fetchDb.get("/api/learn-more");
         setLearnMoreItems(items || []);
      } catch (error) {
         console.error("Error fetching LearnMore items:", error);
         toast({
            title: "Error",
            description: "Failed to load Learn More items",
            variant: "destructive",
         });
      } finally {
         setIsLoading(false);
      }
   };

   const handleCreateNew = () => {
      setCurrentItem({ content: "" });
      setIsEditing(false);
      setActiveTab("edit");
   };

   const handleEdit = (item: LearnMoreItem) => {
      setCurrentItem(item);
      setIsEditing(true);
      setActiveTab("edit");
   };

   const handleDelete = async (id: number) => {
      if (!confirm("Are you sure you want to delete this Learn More item?"))
         return;

      try {
         setIsLoading(true);
         await fetchDb.delete(`/api/learn-more/${id}`);
         toast({
            title: "Success",
            description: "Learn More item deleted",
         });
         fetchLearnMoreItems();
      } catch (error) {
         console.error("Error deleting LearnMore item:", error);
         toast({
            title: "Error",
            description: "Failed to delete Learn More item",
            variant: "destructive",
         });
      } finally {
         setIsLoading(false);
      }
   };

   const handleSave = async () => {
      if (!currentItem || !currentItem.content) {
         toast({
            title: "Error",
            description: "Content is required",
            variant: "destructive",
         });
         return;
      }

      try {
         setIsLoading(true);

         if (isEditing && currentItem.id) {
            // Update existing item
            await fetchDb.patch(
               `/api/learn-more/${currentItem.id}`,
               currentItem
            );
            toast({
               title: "Success",
               description: "Learn More item updated",
            });
         } else {
            // Create new item
            await fetchDb.post("/api/learn-more", currentItem);
            toast({
               title: "Success",
               description: "Learn More item created",
            });
         }

         fetchLearnMoreItems();
         setActiveTab("list");
         setCurrentItem(null);

         if (onContentCreated) {
            onContentCreated();
         }
      } catch (error) {
         console.error("Error saving LearnMore item:", error);
         toast({
            title: "Error",
            description: "Failed to save Learn More item",
            variant: "destructive",
         });
      } finally {
         setIsLoading(false);
      }
   };

   const handleImageSelect = (image: ImageType) => {
      if (currentItem) {
         setCurrentItem({
            ...currentItem,
            imageId: image.id,
            image: image,
         });
      }
      setIsImagePickerOpen(false);
   };

   // Truncate content for preview
   const truncateContent = (content: string, maxLength = 100) => {
      if (content.length <= maxLength) return content;
      return content.substring(0, maxLength) + "...";
   };

   return (
      <>
         <Button
            variant="outline"
            size="icon"
            onClick={() => {
               if (initialItemToEdit) {
                  handleEdit(initialItemToEdit);
               } else {
                  handleCreateNew();
               }
               setIsOpen(true);
            }}
         >
            <Edit className="h-4 w-4" />
         </Button>

         <Dialog open={isOpen} onOpenChange={setIsOpen}>
            <DialogTrigger asChild>
               <Button variant="outline">Manage Learn More Content</Button>
            </DialogTrigger>

            <DialogContent className="max-w-[95vw] md:max-w-[80vw] w-full max-h-[90vh] p-0 overflow-hidden flex flex-col">
               <DialogHeader className="px-6 pt-6 pb-2">
                  <DialogTitle className="text-xl font-bold">
                     Manage Learn More Content
                  </DialogTitle>
                  <DialogDescription>
                     Create and edit detailed product information that can be
                     used in dependencies.
                  </DialogDescription>
               </DialogHeader>

               <Tabs
                  value={activeTab}
                  onValueChange={setActiveTab}
                  className="flex-1 flex flex-col overflow-hidden"
               >
                  <div className="border-b px-6">
                     <TabsList>
                        <TabsTrigger value="list">Content Library</TabsTrigger>
                        <TabsTrigger value="edit">
                           {isEditing ? "Edit Content" : "New Content"}
                        </TabsTrigger>
                     </TabsList>
                  </div>

                  <TabsContent
                     value="list"
                     className="flex-1 p-6  flex flex-col"
                  >
                     <div className="flex justify-between mb-4">
                        <h3 className="text-lg font-medium">
                           Learn More Content Library
                        </h3>
                        <Button onClick={handleCreateNew}>
                           <Plus className="h-4 w-4 mr-2" />
                           Create New
                        </Button>
                     </div>

                     {isLoading ? (
                        <div className="flex items-center justify-center flex-1">
                           <p className="text-gray-500">Loading...</p>
                        </div>
                     ) : learnMoreItems.length === 0 ? (
                        <div className="flex items-center justify-center flex-1">
                           <div className="text-center">
                              <p className="text-gray-500 mb-4">
                                 No Learn More content found
                              </p>
                              <Button onClick={handleCreateNew}>
                                 Create Your First Item
                              </Button>
                           </div>
                        </div>
                     ) : (
                        <ScrollArea className="h-[calc(90vh-16rem)]">
                           <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                              {learnMoreItems.map((item) => (
                                 <div
                                    key={item.id}
                                    className="border rounded-lg overflow-hidden shadow-sm"
                                 >
                                    {item.image ? (
                                       <div className="h-40 overflow-hidden">
                                          <img
                                             src={item.image.url}
                                             alt="Learn More illustration"
                                             className="w-full h-full object-cover"
                                          />
                                       </div>
                                    ) : (
                                       <div className="h-40 bg-gray-100 flex items-center justify-center">
                                          <ImageIcon className="h-12 w-12 text-gray-300" />
                                       </div>
                                    )}

                                    <div className="p-4">
                                       <div className="mb-2 text-sm text-gray-500">
                                          ID: {item.id}
                                       </div>
                                       <div className="text-sm text-gray-600 mb-4 h-16 overflow-hidden">
                                          {truncateContent(item.content)}
                                       </div>

                                       <div className="flex space-x-2">
                                          <Button
                                             variant="outline"
                                             size="sm"
                                             className="flex-1"
                                             onClick={() => handleEdit(item)}
                                          >
                                             <Edit className="h-4 w-4 mr-1" />
                                             Edit
                                          </Button>
                                          <Button
                                             variant="destructive"
                                             size="sm"
                                             onClick={() =>
                                                handleDelete(item.id!)
                                             }
                                          >
                                             <Trash2 className="h-4 w-4" />
                                          </Button>
                                       </div>
                                    </div>
                                 </div>
                              ))}
                           </div>
                        </ScrollArea>
                     )}
                  </TabsContent>

                  <TabsContent
                     value="edit"
                     className="flex-1 p-6 overflow-auto"
                  >
                     {currentItem && (
                        <div className="space-y-6">
                           <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                 Content
                              </label>
                              <Textarea
                                 placeholder="Enter detailed product information here..."
                                 className="min-h-[200px]"
                                 value={currentItem.content}
                                 onChange={(e) =>
                                    setCurrentItem({
                                       ...currentItem,
                                       content: e.target.value,
                                    })
                                 }
                              />
                           </div>

                           <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">
                                 Image (Optional)
                              </label>
                              <div className="flex items-center space-x-3">
                                 {currentItem.image ? (
                                    <div className="relative w-24 h-24 border rounded overflow-hidden">
                                       <img
                                          src={currentItem.image.url}
                                          alt="Selected image"
                                          className="w-full h-full object-cover"
                                       />
                                       <Button
                                          variant="destructive"
                                          size="icon"
                                          className="absolute top-1 right-1 h-6 w-6"
                                          onClick={() =>
                                             setCurrentItem({
                                                ...currentItem,
                                                imageId: null,
                                                image: null,
                                             })
                                          }
                                       >
                                          <Trash2 className="h-3 w-3" />
                                       </Button>
                                    </div>
                                 ) : (
                                    <div className="w-24 h-24 border rounded flex items-center justify-center bg-gray-50">
                                       <ImageIcon className="h-8 w-8 text-gray-300" />
                                    </div>
                                 )}

                                 <Button
                                    variant="outline"
                                    onClick={() => setIsImagePickerOpen(true)}
                                 >
                                    {currentItem.image
                                       ? "Change Image"
                                       : "Select Image"}
                                 </Button>
                              </div>
                           </div>

                           <div className="flex justify-end space-x-2 pt-4">
                              <Button
                                 variant="outline"
                                 onClick={() => setActiveTab("list")}
                              >
                                 Cancel
                              </Button>
                              <Button onClick={handleSave} disabled={isLoading}>
                                 {isLoading ? "Saving..." : "Save"}
                              </Button>
                           </div>
                        </div>
                     )}
                  </TabsContent>
               </Tabs>
            </DialogContent>
         </Dialog>

         {/* Image Picker Dialog */}
         <ImagePickerDialog
            open={isImagePickerOpen}
            onOpenChange={setIsImagePickerOpen}
            onImageSelect={handleImageSelect}
            dialogTitle="Select Image for Learn More"
         />
      </>
   );
}
